<template>
  <div class="rm-screen">
    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      @refreshMeal="loadMealsForRestaurantCompanies"
    ></report-meals-packages-add-meal>

    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="isLoading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!packingCompanies.length" class="rm-empty">
      {{
        filters.search
          ? $t("report_mobile.no_results_for", { search: filters.search })
          : $t("report_mobile.no_meals")
      }}
    </div>

    <template v-else>
      <p class="rm-muted rm-pack-note">{{ $t("report_mobile.packing_local_note") }}</p>

      <article
        v-for="company in packingCompanies"
        :key="company.key"
        class="rm-card rm-pack"
        :class="{ 'is-open': company.key === openKey }"
      >
        <button
          type="button"
          class="rm-pack__head"
          :aria-expanded="String(company.key === openKey)"
          @click="toggleOpen(company.key)"
        >
          <span class="rm-pack__title">
            <span class="rm-pack__name">{{ company.name }}</span>
            <span class="rm-muted">{{ companySubtitle(company) }}</span>
            <!-- zatvorena firma: za koje smene se pakuje i koliko je spakovano -->
            <span v-if="company.key !== openKey" class="rm-pack__chips">
              <span v-for="group in company.groups" :key="group.id" class="rm-pack__chip">
                <report-shift-tag :shift="group.id"></report-shift-tag>
                <b class="rm-num" :class="{ 'is-complete': group.complete }">{{ group.done }}/{{ group.total }}</b>
              </span>
            </span>
          </span>
          <span
            class="rm-pack__progress-text rm-num"
            :class="{ 'is-complete': company.complete }"
          >
            <v-icon v-if="company.ready" size="18" color="#15803d">mdi-check-circle</v-icon>
            {{ company.done }} / {{ company.total }}
          </span>
          <v-icon
            size="18"
            color="#8a817a"
            class="rm-pack__chevron"
            :class="{ 'is-open': company.key === openKey }"
          >
            mdi-chevron-down
          </v-icon>
        </button>

        <div v-if="company.key === openKey || company.done > 0" class="rm-pack__bar">
          <div
            class="rm-progress"
            :class="{ 'rm-progress--complete': company.complete }"
            role="progressbar"
            :aria-valuenow="company.done"
            aria-valuemin="0"
            :aria-valuemax="company.total"
          >
            <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
          </div>
        </div>

        <template v-if="company.key === openKey">
          <!-- pakuje se i nosi po smeni: svaka smena ima svoj spisak i svoj napredak -->
          <section v-for="group in company.groups" :key="group.id" class="rm-pack__group">
            <div class="rm-pack__shift" :class="{ 'is-complete': group.complete }">
              <report-shift-tag :shift="group.id" long></report-shift-tag>
              <i class="rm-pack__rule"></i>
              <span class="rm-num rm-pack__shift-progress">
                <v-icon v-if="group.complete" size="16" color="#15803d">mdi-check</v-icon>
                {{ group.done }} / {{ group.total }}
              </span>
            </div>
            <ul class="rm-list rm-pack__items">
              <li v-for="item in group.items" :key="item.key">
                <label class="rm-pack__item">
                  <input
                    type="checkbox"
                    :checked="item.packed"
                    :aria-label="`${item.name}, ${$t('report_mobile.shift_n', { n: item.shiftId })}`"
                    @change="togglePacked(company.key, item.key)"
                  />
                  <span class="rm-pack__item-text">
                    <span class="rm-pack__item-name" :class="{ 'is-done': item.packed }">
                      {{ item.name }}
                    </span>
                    <span v-if="item.badges.length" class="rm-badges">
                      <span v-for="badge in item.badges" :key="badge" class="rm-badge">
                        {{ badge }}
                      </span>
                    </span>
                  </span>
                  <span class="rm-pack__item-count rm-num" :class="{ 'is-done': item.packed }">
                    {{ item.count }}
                  </span>
                </label>
              </li>
            </ul>
          </section>

          <div class="rm-pack__actions">
            <button
              v-if="isSingleDay"
              type="button"
              class="rm-btn rm-btn--sm"
              @click="openAddDialog(company.source)"
            >
              <v-icon size="18" color="#1c1917">mdi-plus</v-icon>
              {{ $t("report_mobile.add_meal") }}
            </button>
            <button
              v-if="company.ready"
              type="button"
              class="rm-btn rm-btn--success"
              @click="setReady(company.key, false)"
            >
              <v-icon size="20" color="#ffffff">mdi-check</v-icon>
              {{ $t("report_mobile.company_ready") }}
            </button>
            <button
              v-else-if="company.complete"
              type="button"
              class="rm-btn rm-btn--primary"
              @click="setReady(company.key, true)"
            >
              {{ $t("report_mobile.mark_company_ready") }}
            </button>
            <button v-else type="button" class="rm-btn rm-btn--muted" disabled>
              {{ $t("report_mobile.pack_remaining", { n: company.total - company.done }) }}
            </button>
          </div>
        </template>
      </article>
    </template>
  </div>
</template>

<script>
import ReportMealsTabPackages from "@/components/report/meals/tabs/ReportMealsTabPackages.vue";
import hasPlural from "../../shared/hasPlural";
import hasPackingChecklist from "../../shared/hasPackingChecklist";
import hasShiftBinding from "../../shared/hasShiftBinding";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";

/**
 * Mobilni prikaz "Pakovanje": nasleđuje učitavanje, dodavanje obroka, štampu i Excel
 * iz ReportMealsTabPackages. Stanje čekliste ("spakovano") čuva se samo na ovom uređaju.
 */
export default {
  name: "ReportMobilePackages",
  extends: ReportMealsTabPackages,
  mixins: [hasPlural, hasShiftBinding, hasPackingChecklist],
  components: { ReportShiftTag },
  data: () => ({
    /** undefined = automatski (prva nespremna firma), null = sve zatvoreno */
    expandedKey: undefined,
  }),
  computed: {
    openKey() {
      if (this.expandedKey !== undefined) {
        return this.expandedKey;
      }
      const firstPending = this.packingCompanies.find((company) => !company.ready);
      return firstPending ? firstPending.key : null;
    },
  },
  methods: {
    onPackingStateReset() {
      this.expandedKey = undefined;
    },
    onCompanyReady() {
      this.expandedKey = undefined;
    },
    companySubtitle(company) {
      const meals = this.pluralT("report_mobile.meals_n", company.total);
      if (company.key === this.openKey) {
        const shifts =
          company.shiftIds.length === 1
            ? this.$t("report_mobile.shift_n", { n: company.shiftIds[0] })
            : this.$t("report_mobile.shifts_list", { list: company.shiftIds.join(", ") });
        return `${shifts} · ${meals}`;
      }
      if (company.ready) {
        return `${meals} · ${this.$t("report_mobile.status_ready")}`;
      }
      if (company.done === 0) {
        return `${meals} · ${this.$t("report_mobile.status_not_started")}`;
      }
      return `${meals} · ${this.$t("report_mobile.status_in_progress")}`;
    },
    toggleOpen(key) {
      this.expandedKey = this.openKey === key ? null : key;
    },
  },
};
</script>

<style lang="scss">
.rm-pack-note {
  padding: 0 4px;
}

.rm-theme .rm-pack {
  display: flex;
  flex-direction: column;

  &__head {
    width: 100%;
    min-height: 68px;
    padding: 14px 12px 14px 16px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    color: var(--rm-text);
  }

  &__title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 17px;
    font-weight: 700;
  }

  &.is-open &__name {
    font-size: 18px;
  }

  &__progress-text {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;

    &.is-complete {
      color: var(--rm-success);
    }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: 6px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;

    b.is-complete {
      color: var(--rm-success);
    }
  }

  &__chevron {
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__bar {
    padding: 0 16px 12px;
  }

  &.is-open &__bar {
    border-bottom: 1px solid var(--rm-divider);
  }

  &__items li + li {
    border-top: 1px solid var(--rm-divider-soft);
  }

  // zaglavlje jedne smene: oznaka smene, linija i spakovano / ukupno
  &__shift {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px 8px;
    background: var(--rm-bg);
    font-size: 14px;
    font-weight: 700;
    color: var(--rm-muted);
  }

  &__rule {
    flex: 1;
    height: 1px;
    background: var(--rm-divider);
  }

  &__shift-progress {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 60px;
    padding: 8px 16px;
    cursor: pointer;

    input {
      width: 24px;
      height: 24px;
      margin: 0;
      flex-shrink: 0;
    }
  }

  &__item-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__item-name {
    font-size: 16px;
    font-weight: 500;

    &.is-done {
      color: var(--rm-muted);
      text-decoration: line-through;
    }
  }

  &__item-count {
    font-size: 22px;
    font-weight: 700;

    &.is-done {
      color: var(--rm-muted);
    }
  }

  &__actions {
    padding: 12px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px solid var(--rm-divider-soft);
  }
}

.rm-theme .rm-pack__group + .rm-pack__group .rm-pack__shift {
  border-top: 1px solid var(--rm-divider);
}

.rm-theme .rm-pack__shift.is-complete .rm-pack__shift-progress {
  color: var(--rm-success);
}

.rm-theme .rm-btn--success {
  border: none;
  background: var(--rm-success);
  color: #ffffff;
}
</style>
